import { jwtDecode } from "@/features/auth/invalid-token-error";
import { jwtPayload } from "@/features/auth/types";
import { setJwt } from "@/features/auth/utils";
import type { ConfirmationResponse } from "@/features/register/types";
import { AuthApiClient } from "@/features/user/api-client/api-client";
import { factory_account_create_mirror } from "@/features/user/factories";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import useCaptchaVerify from "@/features/utils/hooks/use_captcha_verify";
import type { SignupState } from "@/models/signup";
import { useAppDispatch } from "@/redux/hooks";
import { setExpirationToken, setLinkedCompanies, setRoles } from "@/redux/slices/userSlice";
import { type UseMutationOptions, useMutation } from "@tanstack/react-query";

export type UseCreateAccountProps = {
  signupState: SignupState;
  email: string;
  password: string;
};

export const useCreateAccountMirror = (
  options?: UseMutationOptions<ConfirmationResponse, Error, UseCreateAccountProps>
) => {
  const { getLanguage } = usei18n();
  const dispatch = useAppDispatch();
  const { handleReCaptchaVerify } = useCaptchaVerify();

  return useMutation<ConfirmationResponse, Error, UseCreateAccountProps>({
    mutationFn: async ({ signupState, password, email }: UseCreateAccountProps) => {
      const staticData = await AuthApiClient.getStaticData("fr");
      const codeLanguage = getLanguage();
      const captchaToken = await handleReCaptchaVerify();
      const accountCreatePayload = factory_account_create_mirror(
        email,
        password,
        captchaToken ?? "",
        codeLanguage,
        signupState,
        staticData
      );
      const { token } = await AuthApiClient.createAccountMirror(accountCreatePayload);

      const decoded: jwtPayload = jwtDecode(token);
      const { exp, roles, linked_companies } = decoded;
      if (roles) dispatch(setRoles({ roles: roles }));
      dispatch(setLinkedCompanies({ linkedCompanies: linked_companies }));
      dispatch(setExpirationToken(Number(exp)));
      setJwt({ token: token });

      return { success: true, message: "" };
    },
    ...options
  });
};
